import React, { ReactNode } from 'react';

// Components
import { Button } from '@capintel/component-lib/button';
import { CloseButton } from '@/views/components/buttons';
import LoadingScreen from '@/views/components/common/LoadingScreen';
import { FormattedMessageV2 } from '@/views/components/i18n';
import { withErrorBoundary } from '@/views/components/errors';
import Modal from '@/views/components/modals/AccessibleModal';
import { Typography } from '@capintel/component-lib/typography';

// Libraries
import classNames from 'classnames';

// Utils
import Dictionary from '@/types/dictionary';
import { ALLOWED_HTML_TAGS } from '../i18n/FormattedMessageV2';
import { formatMessage } from '@/utils/i18n';
import { IconName } from '@capintel/component-lib/icon';

export type LoadingState = {
  isActive: boolean;
  titleToLocalize?: string;
  labelToLocalize?: string;
};

export type DefaultFooterButtons = {
  cancelDisabled?: boolean;
  cancelIcon?: IconName;
  cancelLabelToLocalize?: string;
  cancelOnClick?: () => void;
  primaryActionLoading?: boolean;
  primaryDisabled?: boolean;
  primaryIcon?: IconName;
  primaryLabelToLocalize?: string;
  primaryOnClick?: () => void;
  secondaryActionLoading?: boolean;
  secondaryDisabled?: boolean;
  secondaryIcon?: IconName;
  secondaryLabelToLocalize?: string;
  secondaryOnClick?: () => void;
};

type Props = {
  backdropClassName?: string;
  body?: React.FC | ReactNode;
  defaultFooterButtons?: DefaultFooterButtons;
  footer?: React.FC | ReactNode;
  support?: React.FC | ReactNode;
  loading?: LoadingState;
  modalClassName?: string;
  show: boolean;
  size?: 'sm' | 'lg' | 'xl';
  title: string;
  titleI18nParams?: Dictionary<string | number>;
  onClose: () => void;
};

const ModalV2 = (props: Props) => {
  const { backdropClassName, body, loading, modalClassName, show, size = 'sm', title, titleI18nParams, onClose } = props;

  // Validate if we have the correct structure for default footer buttons, otherwise render the footer prop
  const renderDefaultFooter = () => {
    const { defaultFooterButtons } = props;
    const {
      cancelLabelToLocalize,
      cancelIcon,
      cancelOnClick,
      cancelDisabled,
      primaryLabelToLocalize,
      primaryIcon,
      primaryOnClick,
      primaryActionLoading,
      primaryDisabled,
      secondaryLabelToLocalize,
      secondaryIcon,
      secondaryDisabled,
      secondaryOnClick,
      secondaryActionLoading,
    } = defaultFooterButtons ?? {};

    const shouldRenderCancel = cancelLabelToLocalize && cancelOnClick;
    const shouldRenderPrimary = primaryLabelToLocalize && primaryOnClick;
    const shouldRenderSecondary = secondaryLabelToLocalize && secondaryOnClick;

    if (!shouldRenderCancel && !shouldRenderPrimary && !shouldRenderSecondary) {
      // If no default buttons are provided, render the footer prop if provided
      return <></>;
    }

    return (
      <Modal.Footer>
        {shouldRenderCancel && (
          <Button variant="secondary" icon={cancelIcon} onClick={cancelOnClick} disabled={cancelDisabled}>
            {formatMessage(cancelLabelToLocalize)}
          </Button>
        )}
        <div className="modal-footer-action-buttons">
          {shouldRenderSecondary && (
            <Button variant="secondary" onClick={secondaryOnClick} icon={secondaryIcon} disabled={secondaryDisabled} loading={secondaryActionLoading}>
              {formatMessage(secondaryLabelToLocalize)}
            </Button>
          )}
          {shouldRenderPrimary && (
            <Button variant="primary" onClick={primaryOnClick} icon={primaryIcon} disabled={primaryDisabled} loading={primaryActionLoading}>
              {formatMessage(primaryLabelToLocalize)}
            </Button>
          )}
        </div>
      </Modal.Footer>
    );
  };

  const renderFooter = (): ReactNode => {
    if (loading?.isActive) {
      return <></>;
    }

    const { footer } = props;

    if (footer) {
      return <Modal.Footer>{footer}</Modal.Footer>;
    }

    return renderDefaultFooter();
  };

  const renderSupport = (): ReactNode => {
    const { support } = props;

    if (support) {
      return <div className="modal-support">{support}</div>;
    }

    return null;
  };

  const renderBody = (): ReactNode => {
    if (loading?.isActive) {
      return <LoadingScreen messageToLocalize={loading?.labelToLocalize} />;
    }

    return body;
  };

  return (
    <Modal
      backdropClassName={classNames('modal-v2-backdrop', backdropClassName)}
      className={classNames('modal-v2', modalClassName)}
      show={show}
      onHide={onClose}
      size={size}
    >
      <Modal.Header>
        {/* Provisional fix. Pending migration to library modal. */}
        <div className="modal-title">
          <Typography as={ALLOWED_HTML_TAGS.H2} color="primary" variant="headerXs">
            <FormattedMessageV2 toLocalize={title} values={titleI18nParams} />
          </Typography>
        </div>
        <CloseButton onClick={onClose} />
      </Modal.Header>

      <Modal.Body>{renderBody()}</Modal.Body>

      {renderFooter()}
      {renderSupport()}
    </Modal>
  );
};

export default withErrorBoundary(ModalV2);
